/**
 * Storezone — Admin (Tenant) Routes
 *
 * All routes here are wrapped in AdminLayout and
 * require an active tenant membership.
 *
 * Business modules (products, orders, etc.) will be added here
 * in future milestones.
 */

import { lazy, Suspense } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { AdminLayout } from '@/layouts/AdminLayout'
import { LoadingPage } from '@/components/states/LoadingPage'

const AdminDashboard = lazy(() =>
  import('@/pages/admin/AdminDashboard').then((m) => ({
    default: m.AdminDashboard,
  })),
)

const SettingsPage = lazy(() =>
  import('@/pages/admin/SettingsPage').then((m) => ({
    default: m.SettingsPage,
  })),
)

const BranchesPage = lazy(() =>
  import('@/pages/admin/BranchesPage').then((m) => ({
    default: m.BranchesPage,
  })),
)

const StaffPage = lazy(() =>
  import('@/pages/admin/StaffPage').then((m) => ({
    default: m.StaffPage,
  })),
)

const TaxonomyPage = lazy(() =>
  import('@/pages/admin/catalog/TaxonomyPage').then((m) => ({
    default: m.TaxonomyPage,
  })),
)

const ProductsPage = lazy(() =>
  import('@/pages/admin/catalog/ProductsPage').then((m) => ({
    default: m.ProductsPage,
  })),
)

const ProductFormPage = lazy(() =>
  import('@/pages/admin/catalog/ProductFormPage').then((m) => ({
    default: m.ProductFormPage,
  })),
)

const InventoryPage = lazy(() =>
  import('@/pages/admin/inventory/InventoryPage').then((m) => ({
    default: m.InventoryPage,
  })),
)

const OrdersPage = lazy(() =>
  import('@/pages/admin/orders/OrdersPage').then((m) => ({
    default: m.OrdersPage,
  })),
)

const InventoryHistoryPage = lazy(() =>
  import('@/pages/admin/inventory/InventoryHistoryPage').then((m) => ({
    default: m.InventoryHistoryPage,
  })),
)

const CustomersPage = lazy(() =>
  import('@/pages/admin/customers/CustomersPage').then((m) => ({
    default: m.CustomersPage,
  })),
)

const CustomerProfilePage = lazy(() =>
  import('@/pages/admin/customers/CustomerProfilePage').then((m) => ({
    default: m.CustomerProfilePage,
  })),
)

const CreditAgingPage = lazy(() =>
  import('@/pages/admin/customers/CreditAgingPage').then((m) => ({
    default: m.CreditAgingPage,
  })),
)

const CollectionsPage = lazy(() =>
  import('@/pages/admin/payments/CollectionsPage').then((m) => ({
    default: m.CollectionsPage,
  })),
)

const SuppliersPage = lazy(() => import('@/pages/admin/purchasing/SuppliersPage').then(m => ({ default: m.SuppliersPage })))
const SupplierDetailsPage = lazy(() => import('@/pages/admin/purchasing/SupplierDetailsPage').then(m => ({ default: m.SupplierDetailsPage })))
const PurchaseOrdersPage = lazy(() => import('@/pages/admin/purchasing/PurchaseOrdersPage').then(m => ({ default: m.PurchaseOrdersPage })))

const PurchaseOrderDetailsPage = lazy(() =>
  import('@/pages/admin/purchasing/PurchaseOrderDetailsPage').then((m) => ({
    default: m.PurchaseOrderDetailsPage,
  })),
)

const TransfersPage = lazy(() =>
  import('@/pages/admin/transfers/TransfersPage').then((m) => ({
    default: m.TransfersPage,
  })),
)

const TransferDetailsPage = lazy(() =>
  import('@/pages/admin/transfers/TransferDetailsPage').then((m) => ({
    default: m.TransferDetailsPage,
  })),
)

const ReturnsPage = lazy(() =>
  import('@/pages/admin/returns/ReturnsPage').then((m) => ({
    default: m.ReturnsPage,
  })),
)

const CreateReturnPage = lazy(() =>
  import('@/pages/admin/returns/CreateReturnPage').then((m) => ({
    default: m.CreateReturnPage,
  })),
)

const ReturnDetailsPage = lazy(() =>
  import('@/pages/admin/returns/ReturnDetailsPage').then((m) => ({
    default: m.ReturnDetailsPage,
  })),
)

const ExpenseCategoriesPage = lazy(() =>
  import('@/pages/admin/expenses/ExpenseCategoriesPage').then((m) => ({
    default: m.ExpenseCategoriesPage,
  })),
)

const ExpensesPage = lazy(() =>
  import('@/pages/admin/expenses/ExpensesPage').then((m) => ({
    default: m.ExpensesPage,
  })),
)

const ReportsPage = lazy(() =>
  import('@/pages/admin/reports/ReportsPage').then((m) => ({
    default: m.ReportsPage,
  })),
)

export function AdminRoutes() {
  return (
    <AdminLayout>
      <Suspense fallback={<LoadingPage />}>
        <Routes>
          <Route path="/"          element={<AdminDashboard />} />
          <Route path="catalog/setup" element={<TaxonomyPage />} />
          <Route path="products"   element={<ProductsPage />} />
          <Route path="products/:id" element={<ProductFormPage />} />
          <Route path="inventory"  element={<InventoryPage />} />
          <Route path="inventory/:id/history" element={<InventoryHistoryPage />} />
          <Route path="orders"     element={<OrdersPage />} />
          <Route path="customers"  element={<CustomersPage />} />
          <Route path="customers/aging" element={<CreditAgingPage />} />
          <Route path="customers/:id" element={<CustomerProfilePage />} />
          <Route path="payments/collections" element={<CollectionsPage />} />
          <Route path="purchasing/suppliers" element={<SuppliersPage />} />
          <Route path="purchasing/suppliers/:id" element={<SupplierDetailsPage />} />
          <Route path="purchasing/orders" element={<PurchaseOrdersPage />} />
          <Route path="purchasing/orders/:id" element={<PurchaseOrderDetailsPage />} />
          <Route path="transfers" element={<TransfersPage />} />
          <Route path="transfers/:id" element={<TransferDetailsPage />} />
          <Route path="returns" element={<ReturnsPage />} />
          <Route path="returns/create" element={<CreateReturnPage />} />
          <Route path="returns/:id" element={<ReturnDetailsPage />} />
          <Route path="expenses" element={<ExpensesPage />} />
          <Route path="expenses/categories" element={<ExpenseCategoriesPage />} />
          <Route path="staff"      element={<StaffPage />} />
          <Route path="branches"   element={<BranchesPage />} />
          <Route path="reports"    element={<ReportsPage />} />
          <Route path="settings"   element={<SettingsPage />} />
          <Route path="*"          element={<Navigate to="/admin" replace />} />
        </Routes>
      </Suspense>
    </AdminLayout>
  )
}
